import { defineStore } from "pinia"
import { apiV1 } from "@/config/axios"
import toasteventbus from "primevue/toasteventbus"
import i18n from "@/languages/i18n"

const { t } = i18n.global

export const useUploadStore = defineStore("upload", {
  state: () => ({
    documents: [],
    selectedDocuments: [],
    isUploading: false,
    uploadProgress: 0,
    currentDocument: null,
    invalidFiles: [],
    targetFolderId: null
  }),

  getters: {
    hasDocuments: (state) => state.documents.length > 0,
    currentProgress: (state) => state.uploadProgress,
    hasSelectedDocuments: (state) => state.selectedDocuments.length > 0
  },

  actions: {
    async loadFoldersList() {
      const { data } = await apiV1.get("/file-manager/all-folders")
      return data.folder || {}
    },

    setSelectedDocuments(documents) {
      this.selectedDocuments = documents
    },

    clearSelectedDocuments() {
      this.selectedDocuments = []
    },

    addDocuments(files, acceptedTypes) {
      const acceptedExtensions = acceptedTypes
        .split(",")
        .map((type) => type.trim())
        .map((type) => (type.startsWith(".") ? type : type.replace("*", "")))

      const validFiles = []
      const invalidFiles = []
      const duplicateFiles = []

      Array.from(files).forEach((file) => {
        const fileExtension = "." + file.name.split(".").pop().toLowerCase()
        const isMimeTypeValid = acceptedExtensions.some((ext) =>
          ext.startsWith(".")
            ? fileExtension === ext
            : file.type.match(new RegExp(ext.replace("/", "\/")))
        )

        const isDuplicate = this.documents.some(
          (doc) => doc.name === file.name && doc.size === file.size
        )

        if (!isMimeTypeValid) {
          invalidFiles.push(file)
        } else if (isDuplicate) {
          duplicateFiles.push(file)
        } else {
          validFiles.push(file)
        }
      })

      const newDocuments = validFiles.map((file) => ({
        id: crypto.randomUUID(),
        file,
        name: file.name,
        size: file.size,
        type: file.type,
        status: "pending",
        documentType: null,
        uploadProgress: 0
      }))

      this.documents.push(...newDocuments)

      let errorMessage = ""

      if (invalidFiles.length > 0) {
        this.invalidFiles = invalidFiles
        const invalidFileNames = invalidFiles.map((f) => f.name).join(", ")
        errorMessage += `The following files are not allowed and have not been added: ${invalidFileNames}`
      }

      if (errorMessage) {
        throw new Error(errorMessage)
      }
    },

    removeDocument(documentId) {
      this.documents = this.documents.filter((doc) => doc.id !== documentId)
    },

    clearDocuments() {
      this.documents = []
      this.selectedDocuments = []
      this.uploadProgress = 0
      this.isUploading = false
      this.currentDocument = null
    },

    async uploadDocuments() {
      if (!this.hasDocuments || this.isUploading) return

      this.isUploading = true
      this.uploadProgress = 0

      this.documents.forEach((doc) => {
        doc.status = "processing"
        doc.uploadProgress = 0
      })

      try {
        const results = await Promise.allSettled(
          this.documents.map((document) => this.uploadSingleDocument(document))
        )

        const successful = results.filter(
          (r) => r.status === "fulfilled"
        ).length

        this.documents = this.documents.filter(
          (_, index) => results[index].status === "rejected"
        )

        if (successful > 0) {
          toasteventbus.emit("add", {
            severity: "success",
            summary: t("views.uploadDocuments.toasts.uploadCompleteSummary"),
            detail: t("views.uploadDocuments.toasts.uploadCompleteDetail", { count: successful }),
            life: 3000
          })
        }

        return results
      } finally {
        this.isUploading = false
        this.currentDocument = null
      }
    },

    async uploadSingleDocument(document) {
      const formData = new FormData()
      formData.append("document", document.file)
      if (this.targetFolderId) {
        formData.append("folder_id", this.targetFolderId)
      }

      try {
        await apiV1.post("/documents/upload", formData, {
          headers: {
            "Content-Type": "multipart/form-data"
          },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            )
            document.uploadProgress = percentCompleted
            this.updateTotalProgress()
          }
        })

        document.status = "completed"
      } catch (error) {
        document.status = "failed"
        document.uploadProgress = 0
        throw error
      }
    },

    updateTotalProgress() {
      const totalProgress = this.documents.reduce(
        (acc, doc) => acc + doc.uploadProgress,
        0
      )
      this.uploadProgress = Math.round(totalProgress / this.documents.length)
    },

    setTargetFolderId(folderId) {
      this.targetFolderId = folderId
    }
  }
})
